import { mockSystemMetrics, mockAgentMetrics, mockApiResponse } from './mockData';
import { apiClient } from './api';

export const monitoringService = {
  async getSystemHealth(): Promise<{
    status: 'healthy' | 'degraded' | 'unhealthy';
    uptime: string;
    total_workspaces: number;
    total_agents: number;
    total_workflows: number;
    total_executions_today: number;
    active_executions: number;
    error_rate_24h: number;
    avg_response_time: number;
  }> {
    try {
      const response = await apiClient.get('/monitoring/system/health');
      return response.data;
    } catch (error) {
      console.error('Error fetching system health:', error);
      // Return mock data as fallback
      return {
        status: 'healthy',
        uptime: '3d 12h 45m',
        total_workspaces: 5,
        total_agents: 12,
        total_workflows: 8,
        total_executions_today: 24,
        active_executions: 2,
        error_rate_24h: 3.2,
        avg_response_time: 245
      };
    }
  },

  async getWorkspaceMetrics(workspaceId: string): Promise<{
    workspace_id: string;
    workspace_name: string;
    total_agents: number;
    total_workflows: number;
    total_executions: number;
    successful_executions: number;
    failed_executions: number;
    success_rate: number;
    avg_execution_time: number;
    total_tokens_used: number;
    last_activity: string | null;
  }> {
    try {
      const response = await apiClient.get(`/monitoring/workspace/${workspaceId}/metrics`);
      return response.data;
    } catch (error) {
      console.error('Error fetching workspace metrics:', error);
      // Return mock data as fallback
      return {
        workspace_id: workspaceId,
        workspace_name: 'Default Workspace',
        total_agents: 3,
        total_workflows: 2,
        total_executions: 15,
        successful_executions: 12,
        failed_executions: 3,
        success_rate: 80,
        avg_execution_time: 12.5,
        total_tokens_used: 1250,
        last_activity: new Date().toISOString()
      };
    }
  },

  async getExecutionTrends(workspaceId: string, days: number = 7): Promise<Array<{
    date: string;
    total_executions: number;
    successful_executions: number;
    failed_executions: number;
    avg_execution_time: number;
  }>> {
    try {
      const response = await apiClient.get(`/monitoring/workspace/${workspaceId}/trends?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching execution trends:', error);
      // Return mock data as fallback
      const trends = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        trends.push({
          date: date.toISOString().split('T')[0],
          total_executions: Math.floor(Math.random() * 10) + 1,
          successful_executions: Math.floor(Math.random() * 8) + 1,
          failed_executions: Math.floor(Math.random() * 3),
          avg_execution_time: Math.random() * 20 + 5
        });
      }
      return trends;
    }
  },

  async getRecentExecutions(workspaceId: string, limit: number = 20): Promise<{
    executions: Array<{
      execution_id: string;
      workflow_id: number;
      workflow_name: string;
      status: 'pending' | 'running' | 'completed' | 'failed';
      execution_time: number | null;
      steps_completed: number;
      total_steps: number;
      error: string | null;
      created_at: string;
      updated_at: string;
    }>;
  }> {
    try {
      const response = await apiClient.get(`/monitoring/workspace/${workspaceId}/executions/recent?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching recent executions:', error);
      // Return mock data as fallback
      return {
        executions: [
          {
            execution_id: 'exec-1',
            workflow_id: 1,
            workflow_name: 'SDLC Workflow',
            status: 'completed',
            execution_time: 15.2,
            steps_completed: 3,
            total_steps: 3,
            error: null,
            created_at: new Date(Date.now() - 300000).toISOString(),
            updated_at: new Date(Date.now() - 285000).toISOString()
          },
          {
            execution_id: 'exec-2',
            workflow_id: 1,
            workflow_name: 'SDLC Workflow',
            status: 'failed',
            execution_time: 8.5,
            steps_completed: 2,
            total_steps: 3,
            error: 'Connection timeout',
            created_at: new Date(Date.now() - 600000).toISOString(),
            updated_at: new Date(Date.now() - 590000).toISOString()
          }
        ]
      };
    }
  },

  async getWorkspaceAlerts(workspaceId: string): Promise<{
    alerts: Array<{
      type: string;
      severity: 'info' | 'warning' | 'critical';
      message: string;
      timestamp: string;
    }>;
  }> {
    try {
      const response = await apiClient.get(`/monitoring/workspace/${workspaceId}/alerts`);
      return response.data;
    } catch (error) {
      console.error('Error fetching workspace alerts:', error);
      // Return mock data as fallback
      return {
        alerts: [
          {
            type: 'high_failure_rate',
            severity: 'warning',
            message: '3 workflow executions failed in the last 24 hours',
            timestamp: new Date().toISOString()
          }
        ]
      };
    }
  },

  async getAgentMetrics(workspaceId: string, limit: number = 10): Promise<Array<{
    agent_id: number;
    agent_name: string;
    total_executions: number;
    successful_executions: number;
    failed_executions: number;
    success_rate: number;
    avg_execution_time: number;
    total_tokens_used: number;
    last_execution: string | null;
  }>> {
    try {
      const response = await apiClient.get(`/monitoring/workspace/${workspaceId}/agents?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching agent metrics:', error);
      // Return mock data as fallback
      return [
        {
          agent_id: 1,
          agent_name: 'Product Manager Agent',
          total_executions: 8,
          successful_executions: 7,
          failed_executions: 1,
          success_rate: 87.5,
          avg_execution_time: 5.2,
          total_tokens_used: 450,
          last_execution: new Date().toISOString()
        },
        {
          agent_id: 2,
          agent_name: 'Technical Analyst',
          total_executions: 6,
          successful_executions: 5,
          failed_executions: 1,
          success_rate: 83.3,
          avg_execution_time: 8.7,
          total_tokens_used: 320,
          last_execution: new Date().toISOString()
        }
      ];
    }
  },

  async getWorkflowMetrics(workspaceId: string, limit: number = 10): Promise<Array<{
    workflow_id: number;
    workflow_name: string;
    total_executions: number;
    successful_executions: number;
    failed_executions: number;
    success_rate: number;
    avg_execution_time: number;
    avg_steps_completed: number;
    last_execution: string | null;
  }>> {
    try {
      const response = await apiClient.get(`/monitoring/workspace/${workspaceId}/workflows?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow metrics:', error);
      // Return mock data as fallback
      return [
        {
          workflow_id: 1,
          workflow_name: 'SDLC Workflow',
          total_executions: 10,
          successful_executions: 8,
          failed_executions: 2,
          success_rate: 80,
          avg_execution_time: 15.2,
          avg_steps_completed: 2.8,
          last_execution: new Date().toISOString()
        }
      ];
    }
  },

  async getPerformanceMetrics(workspaceId: string, period: string = '7d'): Promise<{
    period: string;
    execution_trends: Array<{
      date: string;
      total_executions: number;
      successful_executions: number;
      failed_executions: number;
      avg_execution_time: number;
    }>;
    top_agents: Array<{
      agent_id: number;
      agent_name: string;
      total_executions: number;
      successful_executions: number;
      failed_executions: number;
      success_rate: number;
      avg_execution_time: number;
      total_tokens_used: number;
      last_execution: string | null;
    }>;
    top_workflows: Array<{
      workflow_id: number;
      workflow_name: string;
      total_executions: number;
      successful_executions: number;
      failed_executions: number;
      success_rate: number;
      avg_execution_time: number;
      avg_steps_completed: number;
      last_execution: string | null;
    }>;
    error_summary: Record<string, number>;
    resource_usage: {
      total_executions: number;
      avg_execution_time: number;
      peak_daily_executions: number;
      success_rate: number;
    };
  }> {
    try {
      const response = await apiClient.get(`/monitoring/workspace/${workspaceId}/performance?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      // Return mock data as fallback
      return {
        period,
        execution_trends: [],
        top_agents: [],
        top_workflows: [],
        error_summary: {},
        resource_usage: {
          total_executions: 0,
          avg_execution_time: 0,
          peak_daily_executions: 0,
          success_rate: 0
        }
      };
    }
  },

  async getSystemMetrics(): Promise<{
    total_agents: number;
    active_agents: number;
    total_workflows: number;
    active_workflows: number;
    total_executions_today: number;
    successful_executions_today: number;
    avg_response_time_today: number;
    total_cost_today: number;
  }> {
    try {
      await mockApiResponse(null, 300);
      return mockSystemMetrics;
    } catch (error) {
      console.error('Error fetching system metrics:', error);
      throw error;
    }
  },

  async getIntegrationHealth(): Promise<Array<{
    integration_id: string;
    integration_name: string;
    status: 'connected' | 'disconnected' | 'error';
    response_time: number;
    success_rate: number;
    last_sync: string | null;
    error_count_24h: number;
  }>> {
    try {
      await mockApiResponse(null, 350);
      
      return [
        {
          integration_id: '1',
          integration_name: 'Jira Integration',
          status: 'connected',
          response_time: 234,
          success_rate: 0.98,
          last_sync: '2024-01-21T14:30:00Z',
          error_count_24h: 1
        },
        {
          integration_id: '2',
          integration_name: 'GitHub Repository',
          status: 'connected',
          response_time: 156,
          success_rate: 0.99,
          last_sync: '2024-01-21T16:45:00Z',
          error_count_24h: 0
        },
        {
          integration_id: '3',
          integration_name: 'Confluence Wiki',
          status: 'connected',
          response_time: 445,
          success_rate: 0.95,
          last_sync: '2024-01-20T10:15:00Z',
          error_count_24h: 3
        },
        {
          integration_id: '4',
          integration_name: 'Slack Workspace',
          status: 'error',
          response_time: 0,
          success_rate: 0.0,
          last_sync: '2024-01-19T12:00:00Z',
          error_count_24h: 24
        },
        {
          integration_id: '5',
          integration_name: 'Custom API Service',
          status: 'disconnected',
          response_time: 0,
          success_rate: 0.0,
          last_sync: null,
          error_count_24h: 0
        },
        {
          integration_id: '6',
          integration_name: 'Development Database',
          status: 'connected',
          response_time: 89,
          success_rate: 1.0,
          last_sync: '2024-01-21T12:00:00Z',
          error_count_24h: 0
        }
      ];
    } catch (error) {
      console.error('Error fetching integration health:', error);
      throw error;
    }
  },

  async getErrorLogs(page: number = 1, size: number = 20): Promise<{
    logs: Array<{
      id: string;
      timestamp: string;
      level: 'error' | 'warning' | 'info';
      message: string;
      component: string;
      details?: any;
    }>;
    total: number;
    page: number;
    size: number;
  }> {
    try {
      await mockApiResponse(null, 300);
      
      const mockLogs = [
        {
          id: 'log-1',
          timestamp: '2024-01-21T16:45:00Z',
          level: 'error' as const,
          message: 'Slack integration authentication failed',
          component: 'slack_integration',
          details: { error_code: 'AUTH_FAILED', retry_count: 3 }
        },
        {
          id: 'log-2',
          timestamp: '2024-01-21T15:30:00Z',
          level: 'warning' as const,
          message: 'Agent execution timeout exceeded',
          component: 'agent_executor',
          details: { agent_id: '3', timeout: 300000 }
        },
        {
          id: 'log-3',
          timestamp: '2024-01-21T14:20:00Z',
          level: 'error' as const,
          message: 'Failed to access GitHub repository',
          component: 'github_integration',
          details: { repository: 'company/private-repo', status_code: 403 }
        },
        {
          id: 'log-4',
          timestamp: '2024-01-21T13:15:00Z',
          level: 'warning' as const,
          message: 'High memory usage detected',
          component: 'system_monitor',
          details: { memory_usage: 85, threshold: 80 }
        },
        {
          id: 'log-5',
          timestamp: '2024-01-21T12:10:00Z',
          level: 'info' as const,
          message: 'Workflow execution completed successfully',
          component: 'workflow_engine',
          details: { workflow_id: '1', execution_time: 15500 }
        }
      ];
      
      const start = (page - 1) * size;
      const end = start + size;
      const logs = mockLogs.slice(start, end);
      
      return {
        logs,
        total: mockLogs.length,
        page,
        size
      };
    } catch (error) {
      console.error('Error fetching error logs:', error);
      throw error;
    }
  },

  async getUsageStatistics(): Promise<{
    daily_usage: Array<{
      date: string;
      agent_executions: number;
      workflow_executions: number;
      total_tokens: number;
      cost_usd: number;
    }>;
    monthly_summary: {
      total_executions: number;
      total_tokens: number;
      total_cost_usd: number;
      avg_daily_executions: number;
    };
  }> {
    try {
      await mockApiResponse(null, 280);
      
      // Generate mock usage data for the last 30 days
      const daily_usage = [];
      const now = new Date();
      let totalExecutions = 0;
      let totalTokens = 0;
      let totalCost = 0;
      
      for (let i = 29; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const agent_executions = 10 + Math.floor(Math.random() * 30); // 10-40 executions
        const workflow_executions = 3 + Math.floor(Math.random() * 10); // 3-13 executions
        const total_tokens = (agent_executions + workflow_executions) * (500 + Math.floor(Math.random() * 1000)); // 500-1500 tokens per execution
        const cost_usd = total_tokens * 0.00002; // $0.02 per 1000 tokens
        
        daily_usage.push({
          date: date.toISOString().split('T')[0],
          agent_executions,
          workflow_executions,
          total_tokens,
          cost_usd: Math.round(cost_usd * 100) / 100
        });
        
        totalExecutions += agent_executions + workflow_executions;
        totalTokens += total_tokens;
        totalCost += cost_usd;
      }
      
      return {
        daily_usage,
        monthly_summary: {
          total_executions: totalExecutions,
          total_tokens: totalTokens,
          total_cost_usd: Math.round(totalCost * 100) / 100,
          avg_daily_executions: Math.round(totalExecutions / 30)
        }
      };
    } catch (error) {
      console.error('Error fetching usage statistics:', error);
      throw error;
    }
  }
}; 